import { z } from 'zod'

import {
  subscriptionPlanSchema,
  subscriptionStatusSchema,
  subscriptionUsageSchema,
  lockedResourceSchema,
  subscribeRequestSchema,
  changePlanRequestSchema,
  type SubscriptionPlan,
  type SubscriptionStatusResponse,
  type SubscriptionUsage,
  type LockedResource,
  type SubscribeRequest,
  type ChangePlanRequest,
} from '@/types/api/subscription'

import { apiClient } from './client'

export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const response = await apiClient.get('/subscription/plans')
  return z.array(subscriptionPlanSchema).parse(response.data)
}

/** Also fetched in the background at login / workspace switch, where a 403 (no subscription:view) is expected. */
export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  const response = await apiClient.get('/subscription', { suppressErrorToast: true } as object)
  return subscriptionStatusSchema.parse(response.data)
}

export async function getSubscriptionUsage(): Promise<SubscriptionUsage> {
  const response = await apiClient.get('/subscription/usage')
  return subscriptionUsageSchema.parse(response.data)
}

export async function getLockedResources(): Promise<LockedResource[]> {
  const response = await apiClient.get('/subscription/locked-resources')
  return z.array(lockedResourceSchema).parse(response.data)
}

export async function subscribe(data: SubscribeRequest): Promise<SubscriptionStatusResponse> {
  const validated = subscribeRequestSchema.parse(data)
  const response = await apiClient.post('/subscription/subscribe', validated)
  return subscriptionStatusSchema.parse(response.data)
}

export async function changePlan(data: ChangePlanRequest): Promise<SubscriptionStatusResponse> {
  const validated = changePlanRequestSchema.parse(data)
  const response = await apiClient.put('/subscription/change-plan', validated)
  return subscriptionStatusSchema.parse(response.data)
}

export async function cancelSubscription(): Promise<SubscriptionStatusResponse> {
  const response = await apiClient.delete('/subscription/cancel')
  return subscriptionStatusSchema.parse(response.data)
}

export async function undoCancellation(): Promise<SubscriptionStatusResponse> {
  const response = await apiClient.put('/subscription/undo-cancel')
  return subscriptionStatusSchema.parse(response.data)
}